import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
} from 'reactstrap';
import {
  addNewCampaignData,
  editCurrCampaignData,
  getCampaignData,
  newCampaignSaveErrored,
} from '../../actions/campaignActions';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import PromotionRow from '../../components/campaign/promotionRow';
import { useParams, useNavigate } from 'react-router-dom';
import NoRowExistsRow from '../../components/common/noRowExistsRow';

const AddNewCampaignPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignId } = useParams();

  // Redux state
  const campaign = useSelector((state) => state.campaignItem);
  const isSuccess = useSelector(
    (state) => state.isNewCampaignSaveSuccess?.isSavedSuccess,
  );
  const savedCampaign = useSelector((state) => state.campaign);
  const hasErrored = useSelector((state) => state.isNewCampaignSaveErrored);
  const errorMessageType = useSelector(
    (state) => state.isNewCampaignSaveErrored?.Type || 'error',
  );
  const errorMessage = useSelector(
    (state) => state.isNewCampaignSaveErrored?.message || '',
  );
  const isSaving = useSelector((state) => state.isNewCampaignIsSaving);
  const redirectToCampaignListPage = useSelector(
    (state) => state.redirectToCampaignListPage,
  );
  const isLoading = useSelector((state) => state.campaignItemIsLoading);

  // Local state
  const [source, setSource] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [externalId, setExternalId] = useState('');
  const [startDate, setStartDate] = useState(
    campaignId ? moment().add(1, 'days') : null,
  );
  const [endDate, setEndDate] = useState(
    campaignId ? moment().add(30, 'days') : null,
  );
  const [promotions, setPromotions] = useState([]);
  const [emptyRowString] = useState('No Row Exist');
  const [isFormValid, setIsFormValid] = useState(false);
  const [pageTitle, setPageTitle] = useState(campaignId ? 'Edit' : 'Add New');
  const [isSourceEnabled, setIsSourceEnabled] = useState(true);
  const [isCampaignIdEnabled, setIsCampaignIdEnabled] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Error flags
  const [errors, setErrors] = useState({
    source: false,
    campaignName: false,
    campaignDescription: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Fetch campaign data on mount
  useEffect(() => {
    dispatch(newCampaignSaveErrored(false));
    if (campaignId) {
      dispatch(getCampaignData(campaignId));
      setIsFormValid(true);
      setPageTitle('Edit');
      setIsSourceEnabled(false);
      setIsCampaignIdEnabled(false);
    }
  }, [dispatch, campaignId]);

  // Update local state when campaign data changes
  useEffect(() => {
    if (campaignId && campaign) {
      setSource(campaign.source || '');
      setCampaignName(campaign.name || '');
      setCampaignDescription(campaign.description || '');
      setStartDate(campaign.startDate ? moment(campaign.startDate) : null);
      setEndDate(campaign.endDate ? moment(campaign.endDate) : null);
      setPromotions(campaign.promotions || []);
      setExternalId(campaign.externalId || '');
      setIsReadOnly(!!campaign.isReadOnly);
    }
  }, [campaignId, campaign]);

  // Redirect if needed
  useEffect(() => {
    if (redirectToCampaignListPage) {
      navigate('/campaign');
    }
  }, [redirectToCampaignListPage, navigate]);

  // Form validation
  const validateForm = useCallback(
    (sDate = startDate, eDate = endDate) => {
      const newErrors = { ...errors };
      let valid = true;

      const isSDateEmpty = !sDate;
      const isEDateEmpty = !eDate;
      if (isSDateEmpty || isEDateEmpty) {
        newErrors.startDate = true;
        newErrors.endDate = true;
        valid = false;
      } else {
        const sDateFormatted = sDate.format('DD-MMM-YYYY');
        const eDateFormatted = eDate.format('DD-MMM-YYYY');
        const isSameOrBefore =
          moment(eDateFormatted).isSameOrBefore(sDateFormatted);
        if (isSameOrBefore) {
          alert('Invalid date');
          newErrors.startDate = true;
          newErrors.endDate = true;
          valid = false;
        } else {
          newErrors.startDate = false;
          newErrors.endDate = false;
        }
      }
      setErrors(newErrors);
      setIsFormValid(valid);
      return valid;
    },
    [startDate, endDate, errors],
  );

  // Handlers
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'source':
        setSource(value);
        break;
      case 'campaignName':
        setCampaignName(value);
        break;
      case 'campaignDescription':
        setCampaignDescription(value);
        break;
      case 'externalId':
        setExternalId(value);
        break;
      default:
        break;
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    validateForm(date, endDate);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    validateForm(startDate, date);
  };

  const canSubmitSearchForm = () => true; // Placeholder

  const handleSubmitAddForm = (e) => {
    e.preventDefault();
    if (!canSubmitSearchForm()) return;
    const newCampaign = {
      source,
      name: campaignName,
      description: campaignDescription,
      startDate: startDate ? moment(startDate).format('DD-MMM-YYYY') : '',
      endDate: endDate ? moment(endDate).format('DD-MMM-YYYY') : '',
      externalId,
      IsActive: true,
    };

    if (campaignId) {
      newCampaign.id = campaignId;
      dispatch(editCurrCampaignData(newCampaign));
    } else {
      dispatch(addNewCampaignData(newCampaign));
    }
  };

  // Render promotions
  const promotionRows = isLoading ? (
    <Row>
      <Col>
        <BeatLoader color={'#004C5F'} loading data-testid='beat-loader' />
      </Col>
    </Row>
  ) : Array.isArray(promotions) && promotions.length > 0 ? (
    promotions.map((promotion, idx) => (
      <div className='panel panel-default' key={idx}>
        <PromotionRow promotion={promotion} campaignId={campaignId} />
      </div>
    ))
  ) : (
    <NoRowExistsRow emptyRowText={emptyRowString} />
  );

  const errorMessageAlert = (
    <Row>
      <Col md={12}>
        <Alert color='danger'>{errorMessage}</Alert>
      </Col>
    </Row>
  );

  return (
    <div>
      <h4>{pageTitle} Campaign</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Form onSubmit={handleSubmitAddForm}>
              {hasErrored && errorMessage.length > 0 ? errorMessageAlert : null}
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='source' md={1}>
                      Source
                    </Label>
                    <Col md={2} sm={10}>
                      <Input
                        type='select'
                        name='source'
                        id='source'
                        required
                        onChange={handleInputChange}
                        value={source}
                        className={errors.source ? 'error' : ''}
                        disabled={!isSourceEnabled}
                      >
                        <option value='' disabled>
                          Select Source
                        </option>
                        <option value='SB'>StarBos</option>
                        <option value='VE' disabled>
                          Vynamic Engage
                        </option>
                      </Input>
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='externalId' md={1}>
                      Campaign Id
                    </Label>
                    <Col md={2} sm={10}>
                      <Input
                        disabled={!isCampaignIdEnabled}
                        type='text'
                        name='externalId'
                        id='externalId'
                        value={externalId}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                        maxLength='20'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='campaignName' md={1}>
                      Campaign Name
                    </Label>
                    <Col md={5} sm={10}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='campaignName'
                        id='campaignName'
                        value={campaignName}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                        maxLength='50'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='campaignDescription' md={1}>
                      Description
                    </Label>
                    <Col md={10} sm={11}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='campaignDescription'
                        id='campaignDescription'
                        value={campaignDescription}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                        maxLength='200'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='startDate' md={1}>
                      Start Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='startDate'
                        name='startDate'
                        required
                        selected={startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className={errors.startDate ? 'error' : 'form-control'}
                        locale='en-au'
                        maxLength='11'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='endDate' md={1}>
                      End Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='endDate'
                        name='endDate'
                        required
                        selected={endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleEndDateChange}
                        className={errors.endDate ? 'error' : 'form-control'}
                        maxLength='11'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup className='me-sm-2 mb-sm-0'>
                    <Button
                      color='primary'
                      name='btnFilter'
                      id='btnFilter'
                      disabled={!isFormValid || isReadOnly}
                    >
                      Save
                    </Button>
                    <BeatLoader
                      color={'#004C5F'}
                      loading={isSaving}
                      data-testid='beat-loader-saving'
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </Container>
        </CardBody>
      </Card>
      <h4>Promotions</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Col md='1' className='column-header white-border'>
                Promotion Id
              </Col>
              <Col md='7' className='column-header white-border'>
                Name
              </Col>
              <Col md='1' className='column-header white-border'>
                Start Date
              </Col>
              <Col md='1' className='column-header white-border'>
                End Date
              </Col>
              <Col md='1' className='column-header white-border'>
                Status
              </Col>
              <Col md='1' className='column-header white-border'></Col>
            </Row>
            {promotionRows}
          </Container>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddNewCampaignPage;
